import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {
  Observable,
  distinctUntilChanged,
  filter,
  forkJoin,
  skip,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  noWhitespaceValidator,
  wordValidation,
} from 'src/app/directives/word-validators.directive';
import {
  IDropdownPm,
  IPositionNewEmployee,
} from 'src/app/interfaces/dropdown-new-employee.interface';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {IMaternityPeriod} from 'src/app/interfaces/maternity.interface';
import {IShortWorkingPeriod} from 'src/app/interfaces/termination.interface';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {AddEmployeeService} from 'src/app/services/add-employee.service';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {roles} from 'src/app/shared/utils/roles';
import {FileTypes} from 'src/app/shared/utils/type-checker';
import {Utils} from 'src/app/shared/utils/utils';
import {
  EmploymentForm,
  IAccessData,
  Tab,
} from '../../../interfaces/employee.interface';
import {DialogService} from '../../../services/dialog.service';
import {EmployeeService} from '../../../services/employee.service';
import {
  columnTypes,
  ITableRowAction,
  UITableHeader,
} from '../../../shared/ui-kits/table/table.models';
import {MaternityPopupComponent} from './maternity-popup/maternity-popup.component';
import {
  emailValidationPattern,
  onlyLettersRegExp,
  regExpEnglishAndSpace,
} from '../../../validators/letterValidators';
import {
  ILongTermLeaveDialog,
  ILongTermLeaveResponse,
  IMobilization,
  ISabbatical,
  LongTermLeaveType,
} from 'src/app/interfaces/long-term-leave.interface';
import {LongTermLeavePopupComponent} from './long-term-leave/long-term-leave-popup.component';
import {IGeneralEditMode} from 'src/app/interfaces/employee-edit-mode.interface';
import {
  IGeneralPut,
  IGeneralTab,
} from 'src/app/interfaces/general-data.interface';
import {WorkingPeriodPopupComponent} from './working-period-popup/working-period-popup.component';
import {LongTermLeaveService} from 'src/app/services/long-term-leave/long-term-leave.service';
import {IContactPerson} from 'src/app/interfaces/personal-data.interface';
import {WorkingPeriodService} from 'src/app/services/working-period/working-period.service';
import {Departments} from 'src/app/enums/departments';
import {CompanyManagementService} from 'src/app/services/company-management.service';
import {FileManagerService} from 'src/app/services/file-manager/file-manager.service';
import {
  MAX_FILE_SIZE,
  ROW_ACTIONS,
  SURE_MODAL_BUTTONS,
  SURE_MODAL_TEXT,
} from 'src/app/shared/utils/constants';
import {EmployeeStatuses} from 'src/app/interfaces/roles.interace';
import {SureModalService} from 'src/app/services/sure-modal/sure-modal.service';
import {DateTime} from 'luxon';
import {ToastrService} from 'ngx-toastr';

/**
 * The general component
 *
 * Display info about the employee.
 */
@UntilDestroy()
@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
})
export class GeneralComponent implements OnInit {
  /** The entry initialFormValues from the profile component */
  public initialFormValues!: IGeneralTab;

  /** general form */
  public form!: UntypedFormGroup;
  public employeeId: number | null = null;
  /** isEditable property to activate edit mode on general tab */
  public isEditable = false;

  /** data for Assigned mentor table */

  public headersCert: UITableHeader[] = [
    {key: 'mentor', index: 0, type: columnTypes.dropDown},
    {key: 'start', index: 1, type: columnTypes.datePicker},
    {key: 'endDate', index: 2, type: columnTypes.datePicker},
    {key: 'employeeId', index: 3, notVisible: true},
    {key: 'id', index: 4, notVisible: true},
  ];

  public mentorsNameArr = [];
  public startDate!: Date;
  public typeOfTable = 'material-plus';

  /** data for Resignation date popup table */

  public headersCertResignation: UITableHeader[] = [
    {
      key: 'status',
      index: 0,
      type: columnTypes.dropDown,
    },
    {key: 'employmentForm', index: 1, type: columnTypes.dropDown},
    {key: 'startDate', index: 3, type: columnTypes.datePicker},
    {key: 'employeeId', index: 4, notVisible: true},
    {key: 'id', index: 5, notVisible: true},
  ];

  public dataCertResignation: IShortWorkingPeriod[] = [];

  /** data for Maternity period popup table */

  public headersCertMaternity: UITableHeader[] = [
    {
      key: 'startDate',
      index: 0,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {
      key: 'endDate',
      index: 1,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'employeeId', index: 2, notVisible: true},
    {key: 'id', index: 3, notVisible: true},
    {key: 'type', index: 4, notVisible: true},
    {
      key: 'paymentDate',
      index: 5,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'vacationDecision', index: 6, notVisible: true},
    {key: 'comment', index: 7, isEditable: true},
  ];

  /** data for Sabbatical period popup table */

  public headersCertSabbatical: UITableHeader[] = [
    {
      key: 'startDate',
      index: 0,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {
      key: 'endDate',
      index: 1,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'comment', index: 2, isEditable: true},
    {key: 'id', index: 3, notVisible: true},
    {
      key: 'longTermLeaveType',
      index: 4,
      type: columnTypes.dropDown,
      displayedName: 'Type',
    },
    {key: 'employeeId', index: 5, notVisible: true},
  ];
  public dataCertLongTermLeave: (ISabbatical | IMobilization)[] = [];
  public dataCertMaternity: IMaternityPeriod[] = [];
  public dataContactPerson: IContactPerson | null = null;
  public formValueDefault!: IGeneralPut;
  public changed = false;
  public gender: IDropdownObject[] = [];
  public departments: IDropdownObject[] = [];
  public projectManager: IDropdownObject[] = [];
  public unit: IDropdownObject[] = [];
  public lineManager: IDropdownObject[] = [];
  public probationStatus: IDropdownObject[] = [];
  public status: IDropdownObject[] = [];
  public statusValue: string = EmployeeStatuses.Current;
  public employmentForm: IDropdownObject[] = [];
  public allDropdowns!: IGeneralEditMode;
  public idOfEmployee!: number;
  public firstLoad = true;
  public isAccountant!: boolean;
  public isHrManager!: boolean;
  public isCEO!: boolean;
  public isRecruiter!: boolean;
  public isPmManager!: boolean;
  public isAdmin!: boolean;
  public isLineManager!: boolean;
  public ableToEdit: boolean = false;
  public regExpCyrillic = /^[а-яА-ЯіІєЄїЇ`’'-\s]+$/;
  public regExpEnglish = /^[a-zA-Z-]+$/;
  public cardMask: string = '00000';
  public contentInterview = []; // data for the table with interviews
  public isPositionSelected = false; // shows that position is selected
  public departmentItems: IDropdownObject[] = []; // possible items for department
  public subDepartmentItems: IDropdownObject[] = []; // possible items for sub-department
  public employmentFormItems: IDropdownObject[] = []; // possible items for employmentForm
  public positionItems: IPositionNewEmployee[] = []; // possible items for position for selected department
  public subPositionItems: IPositionNewEmployee[] = []; // possible items for position for selected sub-department
  public positionFullItems: IPositionNewEmployee[] = []; // all possible items for position
  public levelItems: IDropdownObject[] = []; // possible items for level for selected position
  public longLevelItems: IDropdownObject[] = []; // possible items for level
  public pmItems: IDropdownPm[] = []; // possible items for pm
  public hrItems: IDropdownObject[] = []; // possible items for hr
  public osItems: IDropdownObject[] = []; // possible items for os
  public onlyLettersRegExp = onlyLettersRegExp; // regExp to enter only letters
  public regExpEnglishAndSpace = regExpEnglishAndSpace;
  public isEqualObjects = Utils.isEqualObjects;
  public validateAllFormFields = Utils.validateAllFormFields;
  public isFieldInvalid = Utils.isFieldInvalid;
  public createEmptyObject = Utils.createEmptyObject;
  public file: UntypedFormControl = new UntypedFormControl(null); // control for photo
  public isContractor = false;
  public commentSrc: SafeResourceUrl | string | ArrayBuffer = '';
  public commentFile: UntypedFormControl = new UntypedFormControl(null);
  public defaultCommentFile: null | Blob = null;
  public downloadBlobFile = Utils.downloadBlobFile;
  public isFormer = false;
  public isFemaleGender: boolean = false;
  @ViewChild('inputCommentFile') inputCommentFile!: ElementRef;
  @ViewChild('saveButton') saveButton!: ElementRef;

  public readonly DELIVERY_DEPARTMENT = 'Delivery';
  public readonly MANAGEMENT_SUBDEPARTMENT = 'Management';
  public readonly FEMALE = 'Female';
  readonly ROW_ACTIONS = ROW_ACTIONS;
  private readonly SURE_BUTTONS = SURE_MODAL_BUTTONS;
  private readonly SURE_TEXT = SURE_MODAL_TEXT;

  get isDepartmentDelivery(): boolean {
    return (
      this.form.get('department')?.value?.name === this.DELIVERY_DEPARTMENT
    );
  }

  get isSubDepartmentManagement(): boolean {
    return (
      this.subDepartmentControl?.value?.name === this.MANAGEMENT_SUBDEPARTMENT
    );
  }

  get isDepartmentSelected(): boolean {
    return this.departmentControl?.value?.name;
  }

  get isSubDepartment(): boolean {
    return this.subDepartmentControl?.value?.name;
  }

  get subDepartmentControl(): AbstractControl {
    return this.form.controls['subDepartment'];
  }

  get departmentControl(): AbstractControl {
    return this.form.controls['department'];
  }

  get formerStatus(): IDropdownObject {
    return this.status.find(el => el.name === EmployeeStatuses.Former)!;
  }

  get isEmployee(): boolean {
    return (
      this.form.get('employmentForm')?.value?.name === EmploymentForm.Employee
    );
  }

  get isAvailableForAction(): boolean {
    return (
      !this.isEditable &&
      (this.isAccountant || this.isPmManager || this.isLineManager) &&
      this.isEmployee
    );
  }

  constructor(
    private dialogService: DialogService,
    private closeHandler: CloseHandlerService,
    private employeeService: EmployeeService,
    private access: AccessManagementService,
    private route: ActivatedRoute,
    private notifier: ToastrService,
    private addEmployeeService: AddEmployeeService,
    private sanitizer: DomSanitizer,
    private detector: ChangeDetectorRef,
    private longTermLeaveService: LongTermLeaveService,
    private workingPeriodService: WorkingPeriodService,
    private companyManagementService: CompanyManagementService,
    private fileManagerService: FileManagerService,
    private sureModalService: SureModalService
  ) {
    this.idOfEmployee = +this.route.parent!.snapshot.params['id'];
  }

  ngOnInit() {
    this.form = this.buildGeneralForm();
    this.ableToEdit = this.checkEditPermissions(
      this.route.parent?.snapshot.data['accessData']
    );
    this.getAllData();
    this.getCommentFile();
    this.getEmployeePhoto();
    this.subscribeToFormValueChanges();
    this.configureCloseHandler();
    this.subscribeToDepartmentChanges();
    this.subscribeToPositionChanges();
    this.subscribeToSubDepartmentChanges();
    this.subscribeToEmploymentFormChanges();
    this.subscribeToProjectManagerChanges();
    this.setPermissions();
  }

  buildGeneralForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      firstName: new UntypedFormControl(null, [
        Validators.required,
        wordValidation(this.regExpEnglish),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      lastName: new UntypedFormControl(null, [
        Validators.required,
        wordValidation(this.regExpEnglish),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      firstNameUA: new UntypedFormControl(null, [
        wordValidation(this.regExpCyrillic),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      middleNameUA: new UntypedFormControl(null, [
        wordValidation(this.regExpCyrillic),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      lastNameUA: new UntypedFormControl(null, [
        wordValidation(this.regExpCyrillic),
        noWhitespaceValidator,
        Validators.maxLength(30),
      ]),
      gender: new UntypedFormControl(null),
      department: new UntypedFormControl(null, [Validators.required]),
      position: new UntypedFormControl(null, [Validators.required]),
      subDepartment: new UntypedFormControl(null),
      qualificationLevel: new UntypedFormControl(null),
      projectManager: new UntypedFormControl(null),
      hr: new UntypedFormControl(null, [Validators.required]),
      pe: new UntypedFormControl({value: null, disabled: true}),
      os: new UntypedFormControl(null),
      cardNumber: new UntypedFormControl(null),
      unit: new UntypedFormControl(null),
      lineManager: new UntypedFormControl(null),
      dateOfJoining: new UntypedFormControl(null, [Validators.required]),
      probationEndDate: new UntypedFormControl(null),
      probationStatus: new UntypedFormControl(null),
      commentFromRecruiter: new UntypedFormControl(
        {value: null, disabled: true},
        [Validators.maxLength(1000)]
      ),
      comment: new UntypedFormControl(null, [Validators.maxLength(1000)]),
      employmentForm: new UntypedFormControl(null, [Validators.required]),
      id: new UntypedFormControl(null),
      workEmail: new UntypedFormControl(null, [
        Validators.maxLength(30),
        Validators.pattern(emailValidationPattern),
      ]),
      anukoLogin: new UntypedFormControl(null, [
        Validators.maxLength(20),
        Validators.pattern(this.onlyLettersRegExp),
      ]),
      firstProject: new UntypedFormControl(null, [Validators.maxLength(30)]),
      contractorWorkingHoursPerWeek: new UntypedFormControl(null),
      unlimitedVacationDays: new UntypedFormControl({
        value: null,
        disabled: true,
      }),
      earlyVacationRegistration: new UntypedFormControl({
        value: null,
        disabled: true,
      }),
    });
  }

  setPermissions() {
    this.isAccountant = this.access.checkPermissions(
      roles.ACCOUNTANT_DEPARTMENT
    );
    this.isHrManager = this.access.checkPermissions(roles.HR_DEPARTMENT);
    this.isCEO = this.access.checkPermissions(roles.CEO);
    this.isRecruiter = this.access.checkPermissions(
      roles.RECRUITMENT_DEPARTMENT
    );
    if (this.isRecruiter) {
      this.access.statusOfEmployee.subscribe(currentStatus => {
        this.ableToEdit = !(currentStatus?.status === EmployeeStatuses.Current);
      });

      Utils.enableControl('commentFromRecruiter', this.form);
      Utils.disableControl('comment', this.form);
    }
    this.isPmManager = this.access.checkPermissions(roles.DELIVERY_DEPARTMENT);
    this.isLineManager = this.access.checkPermissions(roles.LINE_MANAGEMENT);
    if (this.isAccountant || this.isLineManager || this.isPmManager) {
      this.form.get('unlimitedVacationDays')?.enable();
      this.form.get('earlyVacationRegistration')?.enable();
    }
    this.isAdmin = this.access.checkPermissions(roles.ADMIN);
    if (!this.isAdmin && !this.isHrManager) {
      this.form.controls['workEmail'].disable();
      this.form.controls['anukoLogin'].disable();
    }
  }

  subscribeToDepartmentChanges() {
    (this.form.get('department') as AbstractControl).valueChanges
      .pipe(
        untilDestroyed(this),
        filter(data => data)
      )
      .subscribe(value => {
        if (value.name === this.DELIVERY_DEPARTMENT) {
          Utils.enableControl('subDepartment', this.form);
          this.subDepartmentControl.setValidators([Validators.required]);
          this.projectManagerControl.enable();
          this.projectManagerControl.updateValueAndValidity();
        } else {
          Utils.disableControl('subDepartment', this.form);
          this.projectManagerControl.setValue(null);
        }
        this.setLineManagerValidation(!this.isDepartmentDelivery);
        this.positionItems = this.positionFullItems.filter(
          item => item.department.id === value.id
        );
        if (this.isEditable) {
          this.form.controls['position'].setValue(null);
          this.form.controls['qualificationLevel'].setValue(null);
          this.subDepartmentControl.setValue(null);
        }
        this.detector.detectChanges();
      });
  }

  subscribeToSubDepartmentChanges() {
    this.subDepartmentControl.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(data => data)
      )
      .subscribe(value => {
        this.setLineManagerValidation(this.isSubDepartmentManagement);
        if (this.isSubDepartmentManagement) {
          this.projectManagerControl.setValue(null);
        }
        if (this.isEditable) {
          this.form.controls['position'].setValue(null);
          this.form.controls['qualificationLevel'].setValue(null);
        }
        this.subPositionItems = this.positionFullItems.filter(
          item => item.department.name === value.name
        );
      });
  }

  subscribeToPositionChanges() {
    (this.form.get('position') as AbstractControl).valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (!value) {
          this.isPositionSelected = false;
          return;
        }
        const positionWhole = this.positionFullItems.find(
          el => el.id === value.id
        );
        if (positionWhole?.qualificationLevelRequired) {
          this.levelItems = this.longLevelItems;
          if (this.isEditable)
            this.form.controls['qualificationLevel'].setValue(null);
          this.isPositionSelected = true;
        } else {
          this.isPositionSelected = false;
        }
        this.detector.detectChanges();
      });
  }

  subscribeToEmploymentFormChanges() {
    this.form
      .get('employmentForm')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        const control = this.form.controls['contractorWorkingHoursPerWeek'];
        const hrControl = this.form.controls['hr'];
        if (!value?.name.includes('Employee')) {
          hrControl.setValue(null);
          hrControl.clearValidators();
          hrControl.updateValueAndValidity();
          control.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(40),
          ]);
          control.updateValueAndValidity();
          this.isContractor = true;
        } else {
          hrControl.setValidators([Validators.required]);
          hrControl.updateValueAndValidity();
          control.setValue(null);
          control.clearValidators();
          control.updateValueAndValidity();
          this.isContractor = false;
        }
      });
  }

  subscribeToProjectManagerChanges() {
    this.form
      .get('projectManager')
      ?.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(value => {
        this.form.get('firstProject')?.setValue(null);
        this.form.get('firstProject')?.setValidators(Validators.required);
        if (value) {
          const unit = this.pmItems.find(v => v.id === value.id)?.unit;
          if (unit) this.form.get('unit')?.setValue(unit);
        }
      });
  }

  getEmployeePhoto() {
    this.employeeService
      .getEmployeePhoto(this.idOfEmployee)
      .pipe(
        untilDestroyed(this),
        filter(value => !!value)
      )
      .subscribe(data => {
        this.file.setValue(data);
      });

    this.subscribeToDateOfJoiningChanges();
  }

  subscribeToDateOfJoiningChanges() {
    this.form
      .get('dateOfJoining')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        filter(data => data),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((date: string) => {
        this.setMinProbationEnd();
        if (this.statusValue !== EmployeeStatuses.Former)
          this.statusValue = this.checkDateValidity(date)
            ? EmployeeStatuses.Pending
            : EmployeeStatuses.Current;
      });
  }

  setLineManagerValidation(value: boolean): void {
    const lineManagerControl = this.form.controls['lineManager'];
    value
      ? lineManagerControl.setValidators([Validators.required])
      : lineManagerControl.clearValidators();
    lineManagerControl.updateValueAndValidity();
    lineManagerControl.updateValueAndValidity();
  }

  /**
   * Form submit on save button
   * If the form is invalid, call the validate function
   */
  save() {
    if (!this.form.valid || this.isFormer) {
      this.validateAllFormFields(this.form);
      return;
    }
    const cardNumber = this.form.get('cardNumber');
    if (cardNumber!.value) {
      cardNumber?.setValue(+cardNumber?.value);
    } else {
      cardNumber?.setValue(null);
    }
    if (this.form.get('workEmail')!.value === '') {
      this.form.get('workEmail')?.setValue(null);
    }

    const formValue = this.form.getRawValue();
    this.formValueDefault = formValue;

    this.closeHandler.notSaved = false;

    if (formValue.subDepartment && formValue.subDepartment.name) {
      formValue.department = formValue.subDepartment;
    }

    const {subDepartment, ...form} = formValue;

    this.employeeService
      .postTabData(Tab.General, form)
      .pipe(
        untilDestroyed(this),
        switchMap(() => {
          const {firstName, lastName} = form;
          this.employeeService.getEmployeeFullNameStream.next(
            `${firstName} ${lastName}`
          );

          const photo = this.file.value;
          if (photo && 'name' in photo) {
            return this.employeeService.updateEmployeePhoto(
              this.idOfEmployee,
              this.file.value
            );
          }
          return of(null);
        })
      )
      .subscribe(() => {
        if (this.isRecruiter) {
          this.access.statusOfEmployee.next(
            this.form.get('status')?.value.name
          );
        }
        this.isFemaleGender =
          this.form.get('gender')?.value?.name === this.FEMALE;
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
      });

    if (this.defaultCommentFile !== this.commentFile.value) {
      this.commentFile.value
        ? this.updateCommentFile()
        : this.deleteCommentFile();
    }
  }

  /**
   * Show dialog window
   */

  maternityTableActions(
    headersForDisplay: UITableHeader[],
    event?: ITableRowAction
  ): void {
    switch (event?.action) {
      case this.ROW_ACTIONS.EDIT:
        const maternityData = this.dataCertMaternity[event.index];
        this.openMaternityModal(maternityData);
        break;
      case this.ROW_ACTIONS.DELETE:
        const {id} = this.dataCertMaternity[event.index];
        this.deleteMaternity(id);
        break;
      default:
        const maternityDataEmptyData = this.createEmptyObject(
          headersForDisplay,
          this.initialFormValues.id
        );
        this.openMaternityModal(maternityDataEmptyData);
        break;
    }
  }

  deleteMaternity(id: number) {
    const {DELETE, CANCEL} = this.SURE_BUTTONS;
    this.sureModalService
      .confirmAction({
        text: this.SURE_TEXT.DELETE_TEXT('Maternity'),
        acceptLabel: DELETE,
        rejectLabel: CANCEL,
      })
      .pipe(
        untilDestroyed(this),
        switchMap(() =>
          this.employeeService.deleteMaternity(id, this.idOfEmployee)
        )
      )
      .subscribe(result => this.buildTable(result));
  }

  openMaternityModal(maternityData: IMaternityPeriod, event?: ITableRowAction) {
    this.dialogService
      .open(MaternityPopupComponent, {
        data: maternityData,
        settings: {},
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => {
          const {id} = data;
          return id
            ? this.employeeService.updateMaternity(data)
            : this.employeeService.addMaternity(data);
        })
      )
      .subscribe(result => this.buildTable(result));
  }

  longTermLeaveTableActions(
    headersForDisplay: UITableHeader[],
    event?: ITableRowAction
  ): void {
    switch (event?.action) {
      case this.ROW_ACTIONS.EDIT:
        const longTermLeave = this.dataCertLongTermLeave[event.index];
        this.openLongTermLeaveModal(longTermLeave);
        break;
      case this.ROW_ACTIONS.DELETE:
        const {id, type} = this.dataCertLongTermLeave[event.index];
        this.deleteLongTermLeave(id!, this.isMobilization(type));
        break;
      default:
        const longTermLeaveEmptyData = this.createEmptyObject(
          headersForDisplay,
          this.initialFormValues.id
        );
        this.openLongTermLeaveModal(longTermLeaveEmptyData);
        break;
    }
  }

  openLongTermLeaveModal(longTermLeave: ISabbatical | IMobilization) {
    this.dialogService
      .open(LongTermLeavePopupComponent, {
        data: this.getLongTermLeaveModalData(longTermLeave),
        settings: {width: 'initial'},
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => {
          if (this.isMobilization(data.type.id)) {
            const {mobilization, ...remainingLeaveDetails} = data.longTermLeave;
            const body = {...remainingLeaveDetails, ...mobilization};

            return this.processMobilization(body);
          }
          return this.processSabbatical(data.longTermLeave);
        }),

        switchMap(() => this.refreshTable())
      )
      .subscribe();
  }

  private processMobilization(
    mobilization: IMobilization
  ): Observable<ILongTermLeaveResponse> {
    return mobilization.id
      ? this.longTermLeaveService.updateMobilization(mobilization)
      : this.longTermLeaveService.createMobilization(mobilization);
  }

  private processSabbatical(sabbatical: ISabbatical): Observable<void> {
    return sabbatical.id
      ? this.longTermLeaveService.updateSabbatical(sabbatical)
      : this.longTermLeaveService.addSabbatical(sabbatical);
  }

  deleteLongTermLeave(id: number, isMobilization: boolean) {
    const {DELETE, CANCEL} = this.SURE_BUTTONS;

    this.sureModalService
      .confirmAction({
        text: this.SURE_TEXT.DELETE_TEXT(
          isMobilization ? 'Mobilization' : 'Sabbatical'
        ),
        acceptLabel: DELETE,
        rejectLabel: CANCEL,
      })
      .pipe(
        untilDestroyed(this),
        switchMap(() =>
          isMobilization
            ? this.longTermLeaveService.deleteMobilization(id)
            : this.longTermLeaveService.deleteSabbatical(id)
        ),
        switchMap(() => this.refreshTable())
      )
      .subscribe();
  }

  getLongTermLeaveModalData(
    data: Partial<ISabbatical | IMobilization>
  ): Partial<ILongTermLeaveDialog> {
    return {
      ...data,
      createdByOptionList: this.allDropdowns.LineManager.concat(
        this.allDropdowns.PM
      ),
      emergencyContactPerson: this.dataContactPerson!,
    };
  }

  isMobilization(type: number): boolean {
    return type === LongTermLeaveType.Mobilization;
  }

  createWorkingPeriod() {
    const employmentStatus = this.dataCertResignation.length
      ? this.dataCertResignation.filter(res => !res.isCancelled).slice(-1)[0]
          ?.employmentForm ?? this.formerStatus
      : this.form.get('employmentForm')?.getRawValue();

    this.dialogService
      .open(WorkingPeriodPopupComponent, {
        data: {
          lmOptionsList: this.allDropdowns.LineManager,
          pmOptionsList: this.allDropdowns.PM,
          employmentStatus,
          createdBy: this.allUniqueManagers,
          isPMAllowToChoose:
            this.isSubDepartment &&
            this.isDepartmentDelivery &&
            !this.isSubDepartmentManagement,
        },
        settings: {width: '800px'},
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data =>
          this.workingPeriodService.addNewTermination(data, this.idOfEmployee)
        ),
        switchMap(() => this.refreshTable())
      )
      .subscribe(result => {
        this.form.get('status')?.setValue(result.status);
        this.form.get('employmentForm')?.setValue(result.employmentForm);
      });

    return;
  }

  onDeleteWorkingPeriod(index: number) {
    const workingPeriodId: number = this.dataCertResignation[index].id!;
    const {DELETE, CANCEL} = this.SURE_BUTTONS;
    this.sureModalService
      .confirmAction({
        text: this.SURE_TEXT.DELETE_TEXT('Working Period'),
        acceptLabel: DELETE,
        rejectLabel: CANCEL,
      })
      .pipe(
        untilDestroyed(this),
        switchMap(() =>
          this.workingPeriodService.deleteTermination(workingPeriodId)
        ),
        switchMap(() => this.refreshTable())
      )
      .subscribe();
  }

  /**
   * edit mode for general tab, isn't activated by default
   * @param {Event} event event to handle edit mode
   */
  onEdit(event: Event) {
    event.preventDefault();
    if (this.firstLoad) {
      if (this.isAccountant) this.form.controls['unit'].disable();
      this.firstLoad = false;
    }
    this.isEditable = !this.isEditable;
    this.form.markAsUntouched();
  }

  buildTable(data: IGeneralTab) {
    this.dataCertResignation = data.terminations;
    if (this.dataCertResignation.length) {
      this.isFormer = false;
    }
    this.dataCertMaternity = data.maternityPeriods;
    this.dataCertLongTermLeave = [
      ...(data.sabbaticals ?? []),
      ...(data.mobilizationPeriods ?? []),
    ].map(res => ({
      ...res,
      longTermLeaveType: {id: res.type, name: LongTermLeaveType[res.type]},
    }));

    this.dataContactPerson = data.contactPerson;
  }

  /**
   * Function that set the min date for the probation end for 3 months
   */
  setMinProbationEnd() {
    this.startDate = Utils.formatStringToDate(
      this.form.controls['dateOfJoining'].value
    );
    this.startDate.setMonth(this.startDate.getMonth() + 3);
    this.form
      .get('probationEndDate')
      ?.setValue(Utils.formatDateToString([this.startDate]));
  }
  /**
   * Function that forkJoin two request and getting an array of dropdowns
   */
  getAllData() {
    forkJoin([
      this.addEmployeeService.getDropdowns(),
      this.employeeService.getDropdownItems<IGeneralEditMode>(Tab.General),
    ])
      .pipe(
        untilDestroyed(this),
        switchMap(([createEmployeeResponse, generalEmployeeResponse]) => {
          this.pmItems = createEmployeeResponse.pm;
          this.positionFullItems = createEmployeeResponse.position;
          this.longLevelItems = createEmployeeResponse.qualificationLevelLong;
          this.hrItems = createEmployeeResponse.hr;
          this.osItems = createEmployeeResponse.os;
          this.departmentItems = generalEmployeeResponse.Department;
          this.subDepartmentItems = createEmployeeResponse.subDepartment;
          this.gender = generalEmployeeResponse.Gender;
          this.employmentForm = generalEmployeeResponse.EmploymentForm;
          this.lineManager = generalEmployeeResponse.LineManager;
          this.probationStatus = generalEmployeeResponse.ProbationEndStatus;
          this.status = generalEmployeeResponse.StatusType;
          this.allDropdowns = generalEmployeeResponse;
          return this.employeeService.getTabData<IGeneralTab>(
            Tab.General,
            this.idOfEmployee
          );
        })
      )
      .subscribe(res => {
        this.initialFormValues = res;
        this.statusValue = res.status?.name || '';
        this.form.patchValue(res);
        if (!res.department?.name) {
          this.form.get('department')?.setValue(null);
        }
        this.buildTable(res);
        this.form.controls['pe'].setValue(res.pe ? 'Yes' : 'No');
        this.employeeId = this.initialFormValues.id;
        this.unit = this.companyManagementService.companyUnits;
        this.isFemaleGender = res.gender?.name === this.FEMALE;

        this.form
          .get('commentFromRecruiter')
          ?.setValue(this.initialFormValues.commentFromRecruiter);
        setTimeout(() => (this.formValueDefault = this.form.getRawValue()));

        if (!this.isHiddenForRecruiter)
          this.form.controls['probationEndDate'].setValidators(
            Validators.required
          );
      });
  }

  get projectManagerControl(): AbstractControl {
    return this.form.get('projectManager') as AbstractControl;
  }

  getCommentFile(): void {
    this.employeeService
      .getCommentFile(this.idOfEmployee)
      .pipe(
        untilDestroyed(this),
        filter(data => data?.type !== 'text/plain')
      )
      .subscribe(data => {
        this.defaultCommentFile = data;
        this.commentSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(data)
        );
        this.commentFile.setValue(data);
      });
  }

  handleCommentFileInput(event: Event): void {
    const fileToUpload = this.fileManagerService.handleFileInput(
      event,
      [FileTypes.doc, FileTypes.docx, FileTypes.pdf],
      MAX_FILE_SIZE
    );
    if (!fileToUpload) return;
    this.commentSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
      URL.createObjectURL(fileToUpload)
    );
    this.commentFile.setValue(fileToUpload);
  }

  updateCommentFile(): void {
    this.employeeService
      .updateCommentFile(this.idOfEmployee, this.commentFile.value)
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.defaultCommentFile = this.commentFile.value));
  }

  removeCommentFile(): void {
    this.commentFile.setValue(null);
    this.inputCommentFile.nativeElement.value = '';
  }

  deleteCommentFile(): void {
    this.employeeService
      .deleteCommentFile(this.idOfEmployee)
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.defaultCommentFile = this.commentFile.value));
  }

  checkDeleteAvailable(): boolean {
    const lastWorkingPeriod: IShortWorkingPeriod =
      this.dataCertResignation[this.dataCertResignation.length - 1];
    if (lastWorkingPeriod && !lastWorkingPeriod.isCancelled)
      return this.checkDateValidity(lastWorkingPeriod?.startDate);
    return false;
  }

  checkDateValidity(date: string): boolean {
    return Utils.formatStringToDateTime(date) > DateTime.now();
  }

  checkEditPermissions(accessData: IAccessData): boolean {
    if (
      this.access.checkPermissions([
        ...roles.HR_DEPARTMENT,
        ...roles.ACCOUNTANT_DEPARTMENT,
        ...roles.ADMIN,
      ])
    ) {
      return true;
    }

    if (this.access.checkPermissions([...roles.DELIVERY_DEPARTMENT])) {
      if (accessData.employeeData.department.name === Departments.Delivery) {
        return true;
      }
    }

    if (this.access.checkPermissions([...roles.ALL_DEPARTMENTS_HEADS])) {
      if (
        accessData.employeeData.department.name ===
        accessData.userData.department.name
      ) {
        return true;
      }
    }

    if (this.access.checkPermissions([...roles.LINE_MANAGEMENT])) {
      if (
        accessData.employeeData.lineManagerId === accessData.userData.employeeId
      ) {
        return true;
      }
    }

    return false;
  }

  refreshTable(): Observable<IGeneralTab> {
    return this.employeeService
      .getTabData<IGeneralTab>(Tab.General, this.idOfEmployee)
      .pipe(tap(res => this.buildTable(res)));
  }

  configureCloseHandler() {
    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.form.markAsUntouched();
      this.isEditable = false;
      this.form.patchValue(this.formValueDefault);
    });

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });
  }

  subscribeToFormValueChanges() {
    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(
          (value: IGeneralPut) =>
            value &&
            this.isEditable &&
            this.form.dirty &&
            !this.isEqualObjects(value, this.formValueDefault)
        )
      )
      .subscribe((value: IGeneralPut) => {
        this.closeHandler.notSaved = true;
      });
  }

  get isHiddenForRecruiter(): boolean {
    return this.isRecruiter && this.isPending;
  }

  get isPending(): boolean {
    return (
      this.access.statusOfEmployee.value?.status === EmployeeStatuses.Pending
    );
  }

  get allUniqueManagers(): IDropdownObject[] {
    const allManagers = [
      ...this.allDropdowns.LineManager,
      ...this.allDropdowns.PM,
    ] as IDropdownObject[];

    const filterArray = [...new Set(allManagers.map(res => res.id))].map(
      res => allManagers.find(el => el.id === res) as IDropdownObject
    );
    return filterArray;
  }
}
